/**
 * Fiscal years (APP-018 amendment, WP-FISCAL-YEAR). The organization's year starts on the
 * `fiscalYear.start` setting's month and day (default January 1). A fiscal year is named by the
 * calendar year it starts in: with a July 1 start, July 1, 2026 to June 30, 2027 is 2026. With the
 * default start, the fiscal year is the calendar year.
 *
 * These derive a year for something not yet stamped. An award keeps the fiscal year it was stamped
 * with, and a ledger entry the year it was written for, whatever the setting says later.
 */

export interface FiscalStart {
  /** 1–12. */
  readonly month: number;
  /** 1–28, so every year has the day (the setting's own bounds). */
  readonly day: number;
}

export const DEFAULT_FISCAL_START: FiscalStart = { month: 1, day: 1 };

const two = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` for the start of the fiscal year named `year`. */
function startOf(year: number, start: FiscalStart): string {
  return `${String(year).padStart(4, '0')}-${two(start.month)}-${two(start.day)}`;
}

/**
 * The fiscal year a business date (`YYYY-MM-DD`) falls in: the calendar year it starts in. Pass
 * the organization's date (`dateInZone` for an instant), never a UTC-sliced instant (APP-018).
 */
export function fiscalYearOf(date: string, start: FiscalStart): number {
  const year = Number(date.slice(0, 4));
  return date >= startOf(year, start) ? year : year - 1;
}

/** The fiscal year `year`: its first and last days, inclusive. */
export function fiscalYearBounds(year: number, start: FiscalStart): { from: string; to: string } {
  const from = startOf(year, start);
  // The day before the next year's start. The day is never past 28, so the next start exists.
  const next = Date.parse(`${startOf(year + 1, start)}T00:00:00.000Z`);
  const to = new Date(next - 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

/** Whether a start is the calendar year's, so a fiscal year reads as a plain year. */
export function isCalendarYear(start: FiscalStart): boolean {
  return start.month === 1 && start.day === 1;
}
